import * as angular from 'angular';
import { Growl } from 'angular/custom/services/growl.service';
import { TogglePanelCallback } from '../../angular/custom/components/toggle-panel.component';
import { ApiSettingsResource, ApiVariant, Modal } from '../custom';
import { AuthService } from 'app/_services/auth.service';
import { ConfirmModal } from 'app/_services/confirm-modal.factory';
import { CopyToClipboardService } from 'app/_services/copyToClipboardService';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import { TemplateModal } from 'app/_services/template-modal.factory';

class AdminRestApiComponentCtrl implements ng.IController {
    private readonly ssoDisableModal: Modal = {
        title: 'Are you sure you want to disable the PRISMA API service?',
        text:
            'By disabling the PRISMA SSO API service, you will have no access via the API anymore. ' +
            'Charges might still fall due according to the terms and conditions of the ' +
            'PRISMA SSO API service as stipulated in the service contract.'
    };

    private readonly ssoEnableModal: Modal = {
        title: 'Are you sure you want to enable the PRISMA API service?',
        text:
            'By confirming, you agree to the terms and conditions of the PRISMA API for storage operators, ' +
            'as described in the SSO service contract.'
    };

    private readonly ssoCongratulationModal: Modal = {
        template:
            'app/storage-admin/storage-admin-rest-api-congratulations-modal.html'
    };

    public togglingApi: boolean;
    public restApiActive: boolean;
    public restApiToken: string;
    public loading: boolean = true;
    public loadingErrorMsg?: string;
    public loadingErrorMsgSupportSetting?: string;
    public priceVisible: boolean;
    public priceEnabled: string = '200.00 EUR';
    public priceDisabled: string = '0.00 EUR';
    public apiSupported: boolean = false;
    public apiVariant: ApiVariant;

    public packageIdentifier: ShipperPremiumServicePackage;
    public oldPackageIdentifier: ShipperPremiumServicePackage;
    private authenticatedUser: LoginResult;
    private apiSettingsResource: ApiSettingsResource;
    private disableModal;
    private enableModal;
    private congratulationModal;

    constructor(
        private $q: ng.IQService,
        private ssoSettingsResource: ApiSettingsResource,
        private authService: AuthService,
        private growl: Growl,
        private templateModal: TemplateModal,
        private confirmModal: ConfirmModal,
        private errorHandler: ErrorHandler,
        private copyToClipboardService: CopyToClipboardService
    ) {}

    $onInit(): void {
        this.apiSettingsResource = this.ssoSettingsResource;
        this.priceVisible = false;
        this.disableModal = this.ssoDisableModal;
        this.enableModal = this.ssoEnableModal;
        this.congratulationModal = this.ssoCongratulationModal;

        this.authService.userLoginPromise.promise.then(
            (loginResult: LoginResult) => {
                this.authenticatedUser = loginResult;
                this.isApiEnabled();
            }
        );
    }

    public apiSettingVisible(): boolean {
        if (this.apiVariant == 'SSO') {
            return true;
        }
    }

    private isApiEnabled() {
        let apiEnabledPromise = this.apiSettingsResource.restApiIsEnabled(
            { id: this.authenticatedUser.referenceId },
            (res: boolean) => {
                this.restApiActive = res;
                this.loadingErrorMsg = null;
            },
            (err: any) => {
                this.loadingErrorMsg =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'Something went wrong when loading the REST API settings.';
            }
        ).$promise;
        let apiSupportStarted = this.apiSettingsResource.restApiSupported(
            {},
            (res: boolean) => {
                this.apiSupported = res;
                this.loadingErrorMsgSupportSetting = null;
            },
            (err: any) => {
                this.loadingErrorMsgSupportSetting =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'Something went wrong when loading the REST API support settings.';
            }
        ).$promise;
        this.$q
            .all([apiEnabledPromise, apiSupportStarted])
            .finally(() => (this.loading = false));
    }

    public toggleApi(cb: TogglePanelCallback) {
        if (!this.apiSupported) {
            return;
        }

        const unToggle: () => void = () => cb(true);

        this.togglingApi = true;
        const disabling: boolean = !this.restApiActive;

        let result: angular.IPromise<any>;
        if (disabling) {
            result = this.openModal(this.disableModal);
        } else {
            result = this.openModal(this.enableModal);
        }

        result
            .then((servicePackage?: ShipperPremiumServicePackage) => {
                const action: () => angular.IPromise<any> =
                    this.toggleRestApiEnabled;
                action
                    .bind(this)()
                    .then(() => {
                        this.restApiActive = !disabling;
                        this.growl.show('Successfully saved.', 'success');
                        if (this.restApiActive) {
                            this.openModal(this.congratulationModal);
                        }
                    })
                    .catch((err: ng.IHttpPromiseCallbackArg<RestError>) => {
                        this.restApiActive = disabling;
                        this.errorHandler(err);
                    })
                    .finally(unToggle);
            })
            .catch(unToggle);
    }

    public toggleRestApiEnabled(): ng.IPromise<void> {
        return this.apiSettingsResource.restApiSetEnabled(
            { id: this.authenticatedUser.referenceId },
            this.restApiActive,
            (res: JwtTokenDto) => {
                this.restApiToken = res.token;
            }
        ).$promise;
    }

    public generateToken(): void {
        this.apiSettingsResource.restApiToken(
            { id: this.authenticatedUser.referenceId },
            (res: JwtTokenDto) => {
                this.restApiToken = res.token;
            },
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                const errorMessage =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'Something went wrong when generating the token. Please check back later.';
                const errorCode =
                    err && err.data && err.data.errorCode
                        ? err.data.errorCode
                        : '';
                this.growl.show(errorMessage, 'danger', { errorCode });
            }
        );
    }

    public copyTextToClipboard(): void {
        this.copyToClipboardService.copyTextToClipboard(this.restApiToken);
    }

    public openModal(modal: Modal): angular.IPromise<any> {
        if (!!modal.template) {
            return this.templateModal(modal.template).result;
        } else {
            return this.confirmModal(modal.text, modal.title).result;
        }
    }
}

angular.module('app').component('pkpAdminRestApi', {
    controller: AdminRestApiComponentCtrl,
    template: require('app/shipper-admin/admin-rest-api.component.html'),
    bindings: {
        apiVariant: '<'
    }
});
